import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { VenueScene, type SelectedBooth, type Venue, type ProjectedLabel } from '@ce/core';

/**
 * The 3D hall. Drag to orbit, wheel or pinch to zoom, tap a block to select it.
 *
 * All scene construction lives in `@ce/core` so this file only wires up a
 * WebGL renderer, pointer events and the label overlay. The native map does the
 * same job with expo-gl and `<Text>`, against the identical scene.
 */
export function VenueMap({
  venue,
  level,
  highlight,
  height = 330,
  onSelect,
}: {
  venue: Venue;
  level: '1' | '2';
  highlight: string;
  height?: number;
  onSelect: (booth: SelectedBooth | null) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<VenueScene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const [labels, setLabels] = useState<ProjectedLabel[]>([]);

  // Scene + renderer: created once for the life of the component.
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    rendererRef.current = renderer;

    const scene = new VenueScene(venue);
    sceneRef.current = scene;

    const resize = () => {
      const w = host.clientWidth || 380;
      const h = host.clientHeight || height;
      renderer.setSize(w, h, false);
      scene.resize(w, h);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    let raf = 0;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      scene.updateCamera();
      renderer.render(scene.scene, scene.camera);
      const rect = host.getBoundingClientRect();
      setLabels(scene.projectLabels(rect.width, rect.height));
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      scene.dispose();
      renderer.dispose();
      sceneRef.current = null;
      rendererRef.current = null;
      void reduceMotion;
    };
    // Venue identity is stable for a tenant; a bundle swap remounts the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue]);

  // Level switch rebuilds the floor.
  useEffect(() => {
    const scene = sceneRef.current;
    const lvl = venue.levels.find((l) => l.id === level) ?? venue.levels[0];
    if (scene && lvl) scene.build(lvl);
  }, [venue, level]);

  useEffect(() => {
    sceneRef.current?.setHighlight(highlight);
  }, [highlight]);

  // Pointer: orbit on drag, pinch to zoom, tap (under 7px of travel) to select.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const points = new Map<number, PointerEvent>();
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let travelled = 0;
    let pinchDistance: number | null = null;

    const down = (e: PointerEvent) => {
      points.set(e.pointerId, e);
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      travelled = 0;
      try { host.setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
      host.style.cursor = 'grabbing';
    };

    const move = (e: PointerEvent) => {
      if (points.has(e.pointerId)) points.set(e.pointerId, e);
      const scene = sceneRef.current;
      if (!scene) return;

      if (points.size === 2) {
        const [a, b] = [...points.values()];
        const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        if (pinchDistance) scene.zoomByRatio(pinchDistance / d);
        pinchDistance = d;
        travelled = 99;
        return;
      }
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      travelled += Math.abs(dx) + Math.abs(dy);
      scene.orbit(dx, dy);
      lastX = e.clientX;
      lastY = e.clientY;
    };

    const up = (e: PointerEvent) => {
      points.delete(e.pointerId);
      if (points.size < 2) pinchDistance = null;
      if (dragging && travelled < 7) tap(e);
      dragging = points.size > 0;
      host.style.cursor = 'grab';
    };

    const tap = (e: PointerEvent) => {
      const scene = sceneRef.current;
      if (!scene) return;
      const r = host.getBoundingClientRect();
      const block = scene.hitTest(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -((e.clientY - r.top) / r.height) * 2 + 1,
      );
      onSelect(block ? { ...block, zoneLabel: scene.zoneLabelFor(block) } : null);
    };

    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      sceneRef.current?.zoom(e.deltaY * 0.04);
    };

    host.addEventListener('pointerdown', down);
    host.addEventListener('pointermove', move);
    host.addEventListener('pointerup', up);
    host.addEventListener('pointercancel', up);
    host.addEventListener('wheel', wheel, { passive: false });
    return () => {
      host.removeEventListener('pointerdown', down);
      host.removeEventListener('pointermove', move);
      host.removeEventListener('pointerup', up);
      host.removeEventListener('pointercancel', up);
      host.removeEventListener('wheel', wheel);
    };
  }, [onSelect]);

  return (
    <div
      ref={hostRef}
      style={{
        display: 'block', height, position: 'relative', touchAction: 'none', cursor: 'grab',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {labels.map((l) => (
          <div
            key={l.key}
            style={{
              position: 'absolute',
              left: l.x,
              top: l.y,
              transform: 'translate(-50%,-50%)',
              whiteSpace: 'nowrap',
              font: '500 9px/1 var(--font-body)',
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              color: 'rgba(var(--color-text-rgb), 0.5)',
              visibility: l.visible ? 'visible' : 'hidden',
              opacity: sceneRef.current?.labelOpacity(l.key) ?? 1,
            }}
          >
            {l.text}
          </div>
        ))}
      </div>
    </div>
  );
}
